import { Typography } from '@arionbanki/arionui';
import { PROCESS_STATUS } from 'consts/processStatus';
import { formatKennitala } from 'helpers';
import { toBucket } from './Admin.helpers';
import { REASON_MAX } from './BulkCancel.consts';
import type { BulkCancelHook } from './useBulkCancel';

type BulkCancelPreviewStepProps = { bulk: BulkCancelHook };

export function BulkCancelPreviewStep({ bulk }: Readonly<BulkCancelPreviewStepProps>) {
	const { preview, t } = bulk;
	if (!preview) return null;
	return (
		<>
			<Typography TagName="h4">{t('admin_bulk_confirm_title') || 'Confirm cancellation'}</Typography>

			{bulk.warning && <div className="admin-bulk-message admin-bulk-message--warning">{bulk.warning}</div>}

			<WarningSection
				items={preview.duplicates.kennitalas}
				title={t('admin_bulk_duplicates') || 'Duplicates'}
			/>
			<WarningSection items={preview.invalid.entries} title={t('admin_bulk_invalid') || 'Invalid entries'} />
			<WarningSection items={preview.notFound.kennitalas} title={t('admin_bulk_not_found') || 'Not found'} />
			<WarningSection
				items={preview.alreadyCancelledOrExpired.kennitalas}
				title={t('admin_bulk_already_cancelled') || 'Already cancelled or expired'}
			/>

			<div className="admin-bulk__count">
				{t('admin_bulk_matched') || 'Processes to cancel'}: <strong>{preview.matchedProcesses}</strong>
			</div>

			<PreviewTable bulk={bulk} />

			<label className="admin-drawer-label" htmlFor="bulk-reason">
				{t('admin_bulk_reason') || `Reason (ticket reference, max ${REASON_MAX} characters)`}
			</label>
			<input
				className="admin-bulk__reason"
				id="bulk-reason"
				maxLength={REASON_MAX}
				onChange={bulk.onReason}
				placeholder="FM-1234"
				type="text"
				value={bulk.reason}
			/>

			{bulk.error && <div className="admin-bulk-message admin-bulk-message--error">{bulk.error}</div>}

			<div className="admin-drawer-actions__row">
				<button className="admin-drawer-btn admin-drawer-btn--back" onClick={bulk.backToInput} type="button">
					{t('back') || 'Back'}
				</button>
				<button
					className="admin-drawer-btn admin-drawer-btn--confirm-cancel"
					disabled={!bulk.canCancel}
					onClick={bulk.runCancel}
					type="button"
				>
					{t('admin_bulk_confirm_btn') || 'Cancel processes'}
				</button>
			</div>
		</>
	);
}

function WarningSection({ items, title }: Readonly<{ items: string[]; title: string }>) {
	if (!items.length) return null;
	return (
		<div className="admin-bulk-warning">
			<div className="admin-bulk-warning__title">
				{title} ({items.length})
			</div>
			<div className="admin-bulk-warning__items">{items.join(', ')}</div>
		</div>
	);
}

function PreviewTable({ bulk }: Readonly<BulkCancelPreviewStepProps>) {
	const { preview, t } = bulk;
	if (!preview?.processes.length) return null;
	return (
		<div className="admin-table-wrap">
			<table className="admin-table">
				<thead>
					<tr>
						<th>{t('id_no') || 'Kennitala'}</th>
						<th>{t('processStatus') || 'Status'}</th>
						<th>{t('admin_bulk_modified_by') || 'Modified by'}</th>
					</tr>
				</thead>
				<tbody>
					{preview.processes.map(process => (
						<tr className="admin-row" key={process.agreementProcessId}>
							<td>{formatKennitala(process.kennitala)}</td>
							<td>
								<span className={`admin-status admin-status--${toBucket(process.processStatus)}`}>
									{t(PROCESS_STATUS[process.processStatus])}
								</span>
							</td>
							<td>{process.modifiedBy}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}
